import { BaseWidget } from "./BaseWidget";
import { SelectControl } from "../controls/SelectControl";
import { IconButtonGroupControl } from "../controls/IconButtonGroupControl";
import { ColorControl } from "../controls/ColorControl";
import { NestedItemControl } from "../controls/NestedItemControl";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import useWidgetPersona from "./_shared/useWidgetPersona";
import usePrintDisplayMode from "./_shared/usePrintDisplayMode";
import UpcomingIcon from '@mui/icons-material/Upcoming';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { client } from "../../../../hooks/Client";
import { useContext, useEffect, useRef, useCallback } from "react";
import { useFirestoreGeneral } from "../../../../firebase/useFirestore";
import { TemplateDataContext } from "../context/TemplateDataContext";
import { EditorContext } from "../context/EditorContext";
import { fetchTemplateData } from "../utils/fetchTemplateData";
import { migrateActivityRepeater } from "../utils/migrateActivityRepeater";
import ActivityVisualization from "../../../dataVisualizations/ActivityVisualization";
import WidgetSourcePlaceholder from "../components/WidgetSourcePlaceholder";
import LoadingSpinner from "../../../common/LoadingSpinner";
import notDeleted from "../../../../helpers/notDeleted";
import { definitionCollection } from '../../../../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// ActivityWidget — picks one activity and renders its outputs + event-fields
// via the shared `ActivityVisualization`. The editor sidebar offers:
//   - `activityId`   Activity picker
//   - `displayMode`  navigator (default) / stacked
//   - `byNestedId`   collapsable accordion per nested item with graph-type +
//                    colour overrides (persisted keyed by stable ID)
// The persona filter comes from the report (`useWidgetPersona`, a report about
// one deelnemer) — the widget has no filter control of its own.
//
// Legacy `repeater[]` array (old index-keyed shape) is migrated to
// `byNestedId{}` on first widget mount via `migrateActivityRepeater`.

/**
 * Resolve nested items (outputs + event fields) for the given activity.
 * In template mode reads from the memoised template blob; otherwise hits
 * Firestore. Returns the same `[{id, label}, ...]` shape NestedItemControl
 * expects.
 *
 * @param {string} activityId
 * @returns {Promise<Array<{id: string, label: string}>>}
 */
const resolveNestedItems = async (activityId) => {
    if (!activityId) return [];
    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';

    if (isTemplate) {
        try {
            const blob = await fetchTemplateData();
            const outputs = (blob?.data?.outputs?.outputs || []).filter(o => o.ActivityID === activityId);
            const eventForms = (blob?.data?.outputs?.eventForms || []).filter(f => f.ActivityID === activityId);
            const eventFormId = eventForms[0]?.ID || '';
            const eventFields = (blob?.data?.outputs?.eventFields || [])
                .filter(f => f.EventId === eventFormId && f.Type !== 'date' && f.Type !== 'outputs')
                .slice()
                .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
            return [
                ...outputs.map(o => ({ id: o.ID, label: o.Title || o.Output || 'Output' })),
                ...eventFields.map(f => ({ id: f.ID, label: f.Title || f.Type })),
            ];
        } catch (err) {
            console.error('ActivityWidget: failed to fetch template nested items', err);
            return [];
        }
    }

    const [outputsSnap, eventFormsSnap] = await Promise.all([
        definitionCollection('Outputs').where('CompagnyID', '==', client || '').where('ActivityID', '==', activityId).orderBy('Position', 'asc').get(),
        definitionCollection('EventForms').where('CompagnyID', '==', client || '').where('ActivityID', '==', activityId).get(),
    ]);
    const outputs = outputsSnap.docs.map(d => d.data()).filter(notDeleted);
    const eventForms = eventFormsSnap.docs.map(d => d.data()).filter(notDeleted);
    const eventFormId = eventForms[0]?.ID || '';

    let eventFields = [];
    if (eventFormId) {
        const eventFieldsSnap = await definitionCollection('EventFields')
            .where('CompagnyID', '==', client || '')
            .where('EventId', '==', eventFormId)
            .orderBy('Position', 'asc')
            .get();
        eventFields = eventFieldsSnap.docs.map(d => d.data()).filter(notDeleted).filter(f => f.Type !== 'date' && f.Type !== 'outputs');
    }

    return [
        ...outputs.map(o => ({ id: o.ID, label: o.Title || o.Output || 'Output' })),
        ...eventFields.map(f => ({ id: f.ID, label: f.Title || f.Type })),
    ];
};

/**
 * @extends {BaseWidget}
 */
export class ActivityWidget extends BaseWidget {

    getName() { return 'Activiteit'; }
    getCategory() { return 'research'; }
    getIcon() { return <UpcomingIcon />; }

    getControls() {
        return {
            'activityId': new SelectControl({
                label: 'Activiteit',
                description: 'Kies een activiteit',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const blob = await fetchTemplateData();
                        values = blob?.data?.activities || [];
                    } else {
                        const activities = await definitionCollection('Activities')
                            .where('CompagnyID', '==', client || '')
                            .orderBy('Position', 'asc')
                            .get();
                        values = activities.docs.map(doc => doc.data()).filter(notDeleted);
                    }

                    return {
                        choices: values.map(o => ({ value: o.ID, label: o.Activity })),
                        allowSearch: true,
                    };
                },
            }),
            'period': new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de data van deze widget toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            'displayMode': new IconButtonGroupControl({
                label: 'Weergave',
                description: 'Wissel tussen carousel (één item per keer) en gestapeld (alle items onder elkaar)',
                defaultValue: 'navigator',
                options: {
                    buttons: [
                        { value: 'navigator', icon: <ViewCarouselIcon />, tooltip: 'Carousel' },
                        { value: 'stacked', icon: <ViewStreamIcon />, tooltip: 'Gestapeld' },
                    ],
                },
            }),
            'graphColor': new ColorControl({
                label: 'Label- en legendakleur',
                description: 'Kleur van de labels en de legenda.',
                defaultValue: '',
            }),
            'byNestedId': new NestedItemControl({
                label: 'Per output',
                description: 'Stel grafiektype en kleur in per output.',
                parentSettingKey: 'activityId',
                noParentText: 'Kies eerst een activiteit.',
                emptyText: 'Geen outputs gevonden voor deze activiteit.',
                nestedItemsResolver: (activityId) => resolveNestedItems(activityId),
                children: {
                    graphType: new SelectControl({
                        label: 'Grafiektype',
                        defaultValue: '',
                        options: {
                            choices: [
                                { value: '', label: 'Standaard' },
                                { value: 'bar', label: 'Staafdiagram' },
                                { value: 'line', label: 'Lijndiagram' },
                            ],
                        },
                    }),
                    color: new ColorControl({
                        label: 'Kleur',
                        defaultValue: '',
                    }),
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} widget={this} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.activityId
 * @param {'navigator'|'stacked'} [props.displayMode]
 * @param {Object<string, Object<string, any>>} [props.byNestedId]
 * @param {Array<Record<string, any>>} [props.repeater] - Legacy (migrated to byNestedId)
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Widget period setting (PeriodOverrideControl)
 * @param {ActivityWidget} props.widget
 * @returns
 */
const Renderer = ({ activityId, displayMode = 'navigator', byNestedId, graphColor, repeater, period, widget }) => {
    const effectiveDisplayMode = usePrintDisplayMode(displayMode);
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);
    const templateActivityData = isTemplate ? (templateData?.data || null) : null;
    const { range: periodRange, startDate, endDate } = useWidgetPeriod(period);
    const editorCtx = useContext(EditorContext);
    const handleCurrentItemChange = useCallback((item) => {
        editorCtx?.setActiveNestedItemId?.(widget.id, item?.id || null);
    }, [editorCtx, widget.id]);

    // Fetch the activity doc — live or from template blob.
    const liveActivities = useFirestoreGeneral('Activities', 'ID', isTemplate ? '' : (activityId || ''));
    const activity = isTemplate
        ? (templateActivityData?.activities || []).find(a => a.ID === activityId)
        : (Array.isArray(liveActivities) && liveActivities[0]);

    // De deelnemer van het rapport; null = iedereen (docs/features/livereport-per-deelnemer.md).
    const personaId = useWidgetPersona();

    // One-shot migration: project legacy repeater[] onto byNestedId{}.
    // Ref-gated so we only attempt this once the nested items have been
    // resolved at least once (the resolver runs inside ActivityVisualization
    // via the onItemsResolved callback).
    const migrationDoneRef = useRef(false);
    const handleItemsResolved = (items) => {
        if (migrationDoneRef.current) return;
        if (!Array.isArray(items) || items.length === 0) return;
        // Only migrate if there's nothing in byNestedId and something in repeater
        const hasNested = byNestedId && Object.keys(byNestedId).length > 0;
        const hasRepeater = Array.isArray(repeater) && repeater.length > 0;
        if (hasNested || !hasRepeater) {
            migrationDoneRef.current = true;
            return;
        }
        const { byNestedId: migrated, migrated: didMigrate } = migrateActivityRepeater(
            { repeater, byNestedId },
            items.map(it => ({ ID: it.id })),
        );
        if (didMigrate) {
            widget.setSetting('byNestedId', migrated);
        }
        migrationDoneRef.current = true;
    };

    // Reset migration latch when activity changes.
    useEffect(() => {
        migrationDoneRef.current = false;
    }, [activityId]);

    if (!activity) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* Still resolving (hook hasn't answered / template blob loading) →
           render nothing; resolved-but-missing (orphan after a template copy)
           or nothing selected → editor-only select CTA. */
        const stillResolving = isTemplate
            ? !templateData
            : (activityId && !Array.isArray(liveActivities));
        if (stillResolving) return <LoadingSpinner />;
        return <WidgetSourcePlaceholder ctaLabel="Selecteer activiteit" />;
    }

    return (
        <ActivityVisualization
            activity={activity}
            displayMode={effectiveDisplayMode}
            nestedSettings={byNestedId || {}}
            graphColor={graphColor}
            selectedPersonaId={personaId}
            startDate={startDate}
            endDate={endDate}
            periodRange={periodRange}
            templateActivityData={templateActivityData}
            onItemsResolved={handleItemsResolved}
            onCurrentItemChange={handleCurrentItemChange}
        />
    );
};
